import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
// Api
import { ISSUE_COMP_TICKETS } from 'api/ticketing/mutations';
// Types
import {
  IssueCompTickets,
  IssueCompTicketsVariables,
} from 'api/ticketing/types/IssueCompTickets';
import { TicketCompCategory } from 'api/graphql-global-types';
// Helpers
import { runTicketingChange } from 'pages/Events/runTicketingChange';

export type IssueCompsFormValues = {
  tierId: string;
  quantity: number;
  category: TicketCompCategory;
  recipientUserId?: string;
  note?: string;
};

export type IssueComps = {
  /** Resolves to whether the comps were issued; the admin is told either way. */
  issueComps: (
    values: IssueCompsFormValues,
    idempotencyKey: string
  ) => Promise<boolean>;
  issuing: boolean;
};

export const useIssueComps = (ticketEventId: string): IssueComps => {
  const [issueMutation, { loading: issuing }] = useMutation<
    IssueCompTickets,
    IssueCompTicketsVariables
  >(ISSUE_COMP_TICKETS, { refetchQueries: ['GetTicketEventOrders'] });

  const issueComps = useCallback(
    (values: IssueCompsFormValues, idempotencyKey: string): Promise<boolean> =>
      runTicketingChange(
        () =>
          issueMutation({
            variables: {
              input: {
                ticketEventId,
                tierId: values.tierId,
                quantity: values.quantity,
                category: values.category,
                recipientUserId: values.recipientUserId || null,
                note: values.note?.trim() || null,
                idempotencyKey,
              },
            },
          }),
        ({ data }) =>
          ['Comp', data?.issueCompTickets.reference, 'issued']
            .filter(Boolean)
            .join(' '),
        'Failed to issue the comp tickets'
      ),
    [issueMutation, ticketEventId]
  );

  return { issueComps, issuing };
};
